<script lang="ts">
const __DsBlock = {
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_decimal_number.name',
	description: '$t:blocks.input_decimal_number.description',
	type: 'input',
	category: 'input',
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => isPrimitiveTypeDecimal(primitiveType) ? ['number'] : [],
		recommendedDisplays: ['$builtin:DisplayFormattedValue'],
	},
	options: [
		{ name: 'modelValue', model: true },
		{ name: 'min' },
		{ name: 'max' },
		{ name: 'step' },
		{ name: 'placeholder' },
		{ name: 'color', type: 'enum', values: BLOCK_COLOR_OPTIONS },
		{ name: 'variant', type: 'enum', values: BLOCK_INPUT_VARIANT_OPTIONS },
		{ name: 'size', type: 'enum', values: BLOCK_CONTROL_SIZE_OPTIONS },
		{ name: 'block' },
		{ name: 'disabled' },
		{ name: 'readonly' },
		{ name: 'disabledNotice' },
	],
}
</script>
<script lang="ts" setup>
import type { InputDecimalNumberProps } from '#layers/ui';
import type { InputBlockProps } from '~/types/input-block';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { isNil } from '#layers/base';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { getTypeParams, isPrimitiveTypeDecimal } from '#layers/data-model';
import { useDisabledNotice } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';

interface Props extends
	InputBlockProps,
	Pick<InputDecimalNumberProps, 'placeholder' | 'color' | 'variant' | 'size' | 'min' | 'max' | 'step' | 'precision' | 'scale'> {
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), { block: true });
const model = defineModel<string | null>();

;

const forwarded = reactivePick(props, 'placeholder', 'color', 'variant', 'size', 'min', 'max', 'step', 'readonly');
const { modelValue, disabled, disabledNotice, field } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const typeParams = computed(() => field.value && isPrimitiveTypeDecimal(field.value.type) ? getTypeParams(field.value.type) : undefined);

const precision = computed(() => {
	const fieldPrecision = typeParams.value?.precision;

	if (!isNil(props.precision) && !isNil(fieldPrecision)) {
		return Math.min(props.precision, fieldPrecision);
	}

	return fieldPrecision ?? props.precision;
});

const scale = computed(() => {
	const fieldScale = typeParams.value?.scale;

	if (!isNil(props.scale) && !isNil(fieldScale)) {
		return Math.min(props.scale, fieldScale);
	}

	return fieldScale ?? props.scale;
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInputDecimalNumber
			v-bind="forwarded"
			v-model="modelValue"
			:precision
			:scale
			:disabled
			:aria-readonly="readonly"
			class="min-w-0"
			:class="{ 'w-full flex-1': block }"
		/>
	</DTooltip>
</template>
